import { currentPortalAccessToken } from './cognito';

export class PortalApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = 'PortalApiError';
  }
}

function apiBase(): string {
  return String(import.meta.env.VITE_PACKPROOF_API_BASE_URL ?? '').trim().replace(/\/$/, '');
}

async function authorizedHeaders(): Promise<HeadersInit> {
  const accessToken = await currentPortalAccessToken();
  return {
    authorization: `Bearer ${accessToken}`,
    accept: 'application/json',
  };
}

async function portalFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      ...(await authorizedHeaders()),
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new PortalApiError(response.status, body.error?.code ?? 'REQUEST_FAILED', body.error?.message ?? 'The portal request failed.');
  }
  return body as T;
}

export type PortalProtocol = {
  hasPackingVideo: boolean;
  hasSealReference: boolean;
  hasArrivalPhoto: boolean;
  hasUnboxingVideo: boolean;
  sellerReferenceComplete: boolean;
  buyerArrivalComplete: boolean;
  outboundComplete: boolean;
};

export type PortalTransaction = {
  id: string;
  object: 'portal_transaction';
  schemaVersion: 1;
  sellerId: string | null;
  buyerId: string | null;
  participantIds: string[];
  status: string;
  title: string;
  category: string;
  description: string;
  priceMinor: number | null;
  currency: string | null;
  identifiers: { label: string; value: string }[];
  conditionNotes: string;
  terms: {
    saleType: 'SHIPPED' | 'LOCAL_HANDOFF';
    shippingResponsibility: 'SELLER' | 'BUYER' | 'NOT_APPLICABLE';
    returns: 'NO_RETURNS' | 'AS_AGREED' | 'PLATFORM_POLICY';
    returnWindowDays: number;
    customTerms: string;
  } | null;
  confirmedBy: string[];
  handoffConfirmedBy: string[];
  completedBy: string[];
  passportId: string | null;
  passportDisplayId: string | null;
  source: { type: string | null; platform: string | null; externalOrderId: string | null } | null;
  protocol: PortalProtocol;
  proof: {
    availability: 'NOT_ELIGIBLE' | 'ELIGIBLE_NOT_ISSUED' | 'AVAILABLE';
    passportId: string | null;
    displayId: string | null;
  };
  workspace: import('@packproof/ux').TransactionWorkspaceProjectionV1;
  lockedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PortalMobileHandoff = {
  object: 'portal_mobile_handoff';
  schemaVersion: 1;
  channel: 'WEB_PORTAL';
  transactionId: string;
  action: string;
  captureOnNativeOnly: true;
  universalLink: string;
  appLink: string;
  storeUrl: string;
};

export type PortalWorkspaceRevision = {
  transactionId: string;
  factsRevision: number;
};

export function getHome() {
  return portalFetch<{ data: { viewerId: string; channel: 'WEB_PORTAL'; transactions: PortalTransaction[] } }>('/v1/portal/home');
}

export async function listWorkspaceRevisions(transactionIds?: readonly string[]): Promise<PortalWorkspaceRevision[]> {
  const query = transactionIds?.length
    ? `?ids=${encodeURIComponent(transactionIds.join(','))}`
    : '';
  const result = await portalFetch<{ data: PortalWorkspaceRevision[] }>(`/v1/portal/revisions${query}`);
  return result.data;
}

export function listTransactions() {
  return portalFetch<{ data: PortalTransaction[] }>('/v1/portal/transactions');
}

export function getTransaction(id: string) {
  return portalFetch<{ data: PortalTransaction }>(`/v1/portal/transactions/${encodeURIComponent(id)}`);
}

export function getTimeline(id: string) {
  return portalFetch<{ data: Array<{ id: string; type: string; summary: string; occurredAt: string }> }>(`/v1/portal/transactions/${encodeURIComponent(id)}/timeline`);
}

export function getPassport(id: string) {
  return portalFetch<{ data: Record<string, unknown> }>(`/v1/portal/transactions/${encodeURIComponent(id)}/proof`);
}

export function issueProofIdentity(id: string) {
  return portalFetch<{ data: {
    availability: 'AVAILABLE';
    passportId: string | null;
    displayId: string | null;
  } }>(`/v1/portal/transactions/${encodeURIComponent(id)}/proof/identity`, {
    method: 'POST',
    body: JSON.stringify({ schemaVersion: 1 }),
  });
}

export type IncomingDirectInvitation = {
  invitationId: string;
  transactionId: string;
  sellerId: string;
  sellerDisplayName: string;
  title: string;
  category: string;
  priceMinor: number;
  currency: string;
  createdAt: string;
  expiresAt: string;
  deliveryMode: 'DIRECT';
};

export function listIncomingInvitations() {
  return portalFetch<{ data: { object: 'consumer_incoming_invitation_list'; invitations: IncomingDirectInvitation[] } }>('/v1/consumer/invitations');
}

export async function acceptInvite(code: string): Promise<{ transactionId: string }> {
  const result = await portalFetch<{ data: { transactionId: string } }>('/v1/consumer/invitations/accept', {
    method: 'POST',
    body: JSON.stringify({ code, clientProtocolVersion: 2 }),
  });
  return result.data;
}

export async function acceptDirectInvite(invitationId: string): Promise<{ transactionId: string }> {
  const result = await portalFetch<{ data: { transactionId: string } }>('/v1/consumer/invitations/accept', {
    method: 'POST',
    body: JSON.stringify({ invitationId, clientProtocolVersion: 2 }),
  });
  return result.data;
}

export function confirmTerms(transactionId: string) {
  return portalFetch<{ data: { locked: boolean } }>(
    `/v1/consumer/transactions/${encodeURIComponent(transactionId)}/confirm-terms`,
    { method: 'POST', body: '{}' },
  );
}

export function getSession() {
  return portalFetch<{ data: { actorId: string; channel: 'WEB_PORTAL' } }>('/v1/portal/session');
}

export function getConsumerSession() {
  return portalFetch<{ data: { actorId: string; email: string; displayName: string | null } }>('/v1/consumer/session');
}

export async function bootstrapAccount(idToken: string): Promise<{ data: { actorId: string; email: string; displayName: string | null; created: boolean } }> {
  const response = await fetch(`${apiBase()}/v1/consumer/bootstrap`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${idToken}`,
      accept: 'application/json',
      'content-type': 'application/json',
    },
    body: '{}',
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new PortalApiError(response.status, body.error?.code ?? 'REQUEST_FAILED', body.error?.message ?? 'Could not bind this PackProof account.');
  }
  return body as { data: { actorId: string; email: string; displayName: string | null; created: boolean } };
}

export type PortalReleaseIdentity = {
  schemaVersion: '1';
  productVersion: string;
  gitSha: string;
  environment: 'local' | 'staging' | 'production';
  deployedAt: string | null;
};

export async function getReleaseIdentity(): Promise<{ data: PortalReleaseIdentity }> {
  const response = await fetch(`${apiBase()}/v1/meta`, { headers: { accept: 'application/json' } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new PortalApiError(response.status, body.error?.code ?? 'REQUEST_FAILED', body.error?.message ?? 'Could not load backend identity.');
  }
  return body as { data: PortalReleaseIdentity };
}

export function listEvidence(id: string) {
  return portalFetch<{ data: Array<{
    id: string;
    type: string;
    status: string;
    sha256: string | null;
    workflowReady: boolean;
    finalizedAt: string | null;
  }> }>(`/v1/portal/transactions/${encodeURIComponent(id)}/evidence`);
}

export function createMobileHandoff(id: string, action: string) {
  return portalFetch<{ data: PortalMobileHandoff }>(`/v1/portal/transactions/${encodeURIComponent(id)}/mobile-handoff`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

export function toUxTransaction(item: PortalTransaction) {
  return {
    id: item.id,
    sellerId: item.sellerId ?? '',
    buyerId: item.buyerId,
    participantIds: item.participantIds,
    status: item.status as import('@packproof/ux').TransactionStatus,
    title: item.title,
    category: item.category,
    description: item.description,
    priceMinor: item.priceMinor ?? 0,
    currency: item.currency ?? 'USD',
    identifiers: item.identifiers,
    conditionNotes: item.conditionNotes,
    terms: item.terms ?? {
      saleType: 'SHIPPED' as const,
      shippingResponsibility: 'SELLER' as const,
      returns: 'AS_AGREED' as const,
      returnWindowDays: 0,
      customTerms: '',
    },
    confirmedBy: item.confirmedBy,
    handoffConfirmedBy: item.handoffConfirmedBy,
    completedBy: item.completedBy,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    lockedAt: item.lockedAt,
    passportId: item.passportId,
    passportDisplayId: item.passportDisplayId,
    source: item.source
      ? {
          type: item.source.type ?? undefined,
          platform: item.source.platform ?? undefined,
          externalOrderId: item.source.externalOrderId ?? undefined,
        }
      : null,
  };
}
